import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import ListItem from '@mui/material/ListItem';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { Channel } from '../../../api';
import { StatusChip } from '../../../components/StatusChip';
import { reasonMessage } from '../../../i18n';

import { ChannelPhotoAvatar } from './ChannelPhotoAvatar';

// dir="auto": a channel name in Arabic or Hebrew reads right to left (C3 R7). A constant, not a text.
const OWN_DIRECTION = 'auto';

/** Props of {@link ChannelRow}. */
export interface ChannelRowProps {
  readonly channel: Channel;
  /** A tick or untick of this channel is in flight (S5 R4, AC35). */
  readonly busy: boolean;
  readonly onToggle: (channel: Channel) => void;
}

/**
 * One channel of E3 (mockup 4): avatar, name, the affiliate label or the inactive text, and a
 * checkbox named "Copy {name}", or the Inactive chip (S5 R2, R8). Shows no statistic (R9).
 */
export function ChannelRow({ channel, busy, onToggle }: ChannelRowProps): ReactElement {
  const { t } = useTranslation();
  const inactive = reasonMessage('CHANNEL_INACCESSIBLE');
  const detail = !channel.accessible
    ? t(inactive.key, inactive.values)
    : channel.recommendedByAffiliate
      ? t('onboarding.channels.affiliateChannel')
      : null;
  return (
    <ListItem sx={{ gap: 1.5, py: 1.25, px: 1.75 }}>
      <ChannelPhotoAvatar channel={channel} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          component="span"
          dir={OWN_DIRECTION}
          sx={{
            display: 'block',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {channel.title}
        </Box>
        {detail === null ? null : (
          <Box
            component="span"
            sx={(theme) => ({ display: 'block', fontSize: 13, color: theme.fx.textMuted })}
          >
            {detail}
          </Box>
        )}
      </Box>
      {channel.accessible ? (
        <Checkbox
          checked={channel.subscription !== null}
          disabled={busy}
          onChange={() => {
            onToggle(channel);
          }}
          slotProps={{
            input: { 'aria-label': t('onboarding.channels.copy', { name: channel.title }) },
          }}
        />
      ) : (
        <StatusChip tone="danger">{t('onboarding.channels.inactive')}</StatusChip>
      )}
    </ListItem>
  );
}
